import { OddsFormat } from '@bpos';
import { OptionMarketStatus, OptionStatus, Visibility } from '@cds/betting-offer';
import { MessageEnvelope } from '@cds/push';
import {
    isGameDeleteEnvelope,
    isGameUpdateEnvelope,
    isOptionMarketDeleteEnvelope,
    isOptionMarketEnvelope,
    isOptionMarketUpdateEnvelope,
} from '@frontend/sports/content-distribution/feature';
import { OddsOperations } from '@frontend/sports/odds/feature';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { toComboPrevention } from '../../../core/picks/combo-prevention';
import { toMinimumCombo } from '../../../core/picks/minimum-combination';

export class BetslipV1PickUpdater {
    static updateV1Pick(pick: BetslipV1Pick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipPick {
        return isOptionMarketEnvelope(message)
            ? BetslipV1PickUpdater.updateFromOptionMarketUpdate(pick, message, oddsFormat)
            : BetslipV1PickUpdater.updateFromGameUpdate(pick, message, oddsFormat);
    }

    private static updateFromGameUpdate(pick: BetslipV1Pick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipV1Pick {
        if (isGameDeleteEnvelope(message) && message.payload.gameId === pick.market.id) {
            const updatedPick = pick.copy();
            updatedPick.market = { ...pick.market, isVisible: false, isClosed: true };

            return updatedPick;
        }
        if (isGameUpdateEnvelope(message) && message.payload.game.id === pick.market.id) {
            const cdsGame = message.payload.game;
            const updatedPick = pick.copy();
            updatedPick.market = {
                ...pick.market,
                isVisible: cdsGame.visibility === Visibility.Visible,
                isClosed: cdsGame.visibility === Visibility.Hidden,
                comboPrevention: toComboPrevention(cdsGame.combo1),
                minimumCombo: toMinimumCombo(cdsGame.combo2),
            };

            const option = cdsGame.results.find((o) => o.id === pick.option.id)!;
            if (option) {
                const newPrice = {
                    ...pick.currentPrice!,
                    nativeOdds: OddsOperations.createOdds(option),
                    isVisible: option.visibility === Visibility.Visible && option.odds > 1,
                };
                if (
                    !OddsOperations.equal(newPrice.nativeOdds, pick.currentPrice!.nativeOdds, oddsFormat) ||
                    newPrice.isVisible !== pick.currentPrice!.isVisible
                ) {
                    updatedPick.priceHistory.push({ ...pick.currentPrice! });
                }
                updatedPick.option.price = newPrice;
            } else {
                updatedPick.option.price.isVisible = false;
            }

            return updatedPick;
        }

        return pick;
    }

    private static updateFromOptionMarketUpdate(pick: BetslipV1Pick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipV1Pick {
        if (isOptionMarketDeleteEnvelope(message) && message.payload.marketId === pick.market.id) {
            const updatedPick = pick.copy();
            updatedPick.market = { ...pick.market, isVisible: false, isClosed: true };

            return updatedPick;
        }
        if (isOptionMarketUpdateEnvelope(message) && message.payload.optionMarket.id === pick.market.id) {
            const cdsGame = message.payload.optionMarket;
            const updatedPick = pick.copy();
            updatedPick.market = {
                ...pick.market,
                isVisible: cdsGame.status === OptionMarketStatus.Visible,
                isClosed: cdsGame.status === OptionMarketStatus.Hidden,
                comboPrevention: toComboPrevention(cdsGame.comboPrevention),
                minimumCombo: toMinimumCombo(cdsGame.minCombo || 0),
            };

            const option = cdsGame.options.find((o) => o.id === pick.option.id)!;
            if (option) {
                const newPrice = {
                    ...pick.currentPrice!,
                    nativeOdds: OddsOperations.createOdds(option.price),
                    isVisible: !!(option.status === OptionStatus.Visible && option.price.odds && option.price.odds > 1),
                };
                if (
                    !OddsOperations.equal(newPrice.nativeOdds, pick.currentPrice!.nativeOdds, oddsFormat) ||
                    newPrice.isVisible !== pick.currentPrice!.isVisible
                ) {
                    updatedPick.priceHistory.push({ ...pick.currentPrice! });
                }
                updatedPick.option.price = newPrice;
            } else {
                updatedPick.option.price.isVisible = false;
            }

            return updatedPick;
        }

        return pick;
    }
}
